import { CheckIcon } from "lucide-react";
import { AlertDescription, AlertTitle } from "../ui/alert";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Loader2Icon } from "lucide-react";
import { Team } from "@/lib/types";
import { request } from "@/lib/request";
import { tokenManager } from "@/lib/auth/token-manager";
import { toast } from "sonner";
import { useState } from "react";
import { captureEvent, sanitizeErrorReason } from "@/lib/analytics";

export const JoinTeam = ({
  team,
  goBack,
  onJoin,
}: {
  team: Team;
  accessToken?: string;
  goBack: () => void;
  onJoin?: () => void;
}) => {
  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const handleJoin = async () => {
    if (!tokenManager.isSignedIn() || !team) {
      return;
    }

    setJoining(true);
    setJoinError(null);
    captureEvent("onboarding_team_join_submitted", {
      teamId: team.id,
      source: "invite",
    });

    try {
      await request.post(`/team/members/join/${team.id}`, {}).catch(err => {
        throw new Error(
          err.response?.data.message
            ? err.response?.data.message
            : "Failed to join team.",
        );
      });

      toast.success("Successfully joined team.");
      captureEvent("onboarding_team_join_succeeded", {
        teamId: team.id,
        source: "invite",
      });
      goBack();
      onJoin?.();
    } catch (err) {
      captureEvent("onboarding_team_join_failed", {
        teamId: team.id,
        source: "invite",
        reason: sanitizeErrorReason(err),
      });
      setJoinError(err instanceof Error ? err.message : "Failed to join team.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <>
      <p>
        <span className="text-muted-foreground">Invited by: </span>
        <span className="font-bold">{team.owner.displayName}</span>
        {team.owner.displayName === team.owner.email ? null : (
          <span className="text-muted-foreground"> ({team.owner.email})</span>
        )}
      </p>
      <p className="text-sm">
        By joining this team, you can share your dashboards with other team
        members and see their shared dashboards. You can also collaborate with
        your team members on shared dashboards.
      </p>
      <p className="text-sm">
        If you have any questions, please contact the team owner.
      </p>
      {team.members.length ? (
        <>
          <h4 className="text-sm font-bold">Other members in this team:</h4>
          <ul className="flex max-h-[200px] flex-col gap-1 overflow-y-auto">
            {team.members
              .sort((a, b) => Number(b.isJoined) - Number(a.isJoined))
              .slice(0, 10)
              .map(member => (
                <li key={member.email}>
                  <span className="text-muted-foreground">
                    {member.isJoined
                      ? member.displayName
                      : `${member.email} (pending)`}
                  </span>
                  {!member.isJoined ||
                  member.displayName === member.email ? null : (
                    <span className="text-muted-foreground">
                      {" "}
                      ({member.email})
                    </span>
                  )}
                </li>
              ))}
          </ul>
          {team.members.length > 10 && (
            <p className="text-muted-foreground mt-1 text-xs">
              +{team.members.length - 10} more team members not shown
            </p>
          )}
        </>
      ) : null}
      {joinError ? (
        <Alert variant="destructive" className="mt-2">
          <AlertTitle>Failed to join team</AlertTitle>
          <AlertDescription>
            {joinError || "Something went wrong. Please try again."}
          </AlertDescription>
        </Alert>
      ) : null}
      <div className="mt-2 flex justify-end gap-4">
        <Button variant="secondary" onClick={goBack}>
          Go back
        </Button>
        <Button variant="default" onClick={handleJoin} disabled={joining}>
          {joining ? (
            <>
              <Loader2Icon className="animate-spin" />
              Joining team
            </>
          ) : (
            <>
              <CheckIcon />
              Join team
            </>
          )}
        </Button>
      </div>
    </>
  );
};
